<div class="col-12 col-xl-8">
  <div class="entity-binding-workspace bg-white bg-opacity-75 rounded-4 border shadow p-3 h-100">
    <div class="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-1">
      <div>
        <h2 class="fs-5 fw-bold mb-1">Active Document Form Bindings</h2>
        <div class="entity-panel-caption small fw-semibold">
          Bind document forms to {{ entityTypeName || 'this entity' }}.
        </div>
      </div>
      <span class="entity-detail-pill entity-status-active rounded-pill fw-bold text-uppercase">
        {{ bound_form_count }} Active
      </span>
    </div>

    <div class="entity-new-binding-card bg-white rounded-4 border p-3 mb-3 mt-3 shadow">
      <div class="d-flex align-items-start gap-2 mb-3">
        <div class="entity-new-binding-icon rounded-3 d-inline-flex align-items-center justify-content-center">
          <i class="bi bi-link-45deg"></i>
        </div>
        <div>
          <div class="fw-bold text-s mb-1">Add Document Binding</div>
          <div class="entity-panel-caption text-s fw-semibold">
            Only forms with a document form type are shown here for binding.
          </div>
        </div>
      </div>

      <div class="row g-3 align-items-end">
        <div class="col-12 col-lg-9">
          <label class="form-label mb-1 fw-semibold text-s">Select Document Form</label>
          <div ref="formMultiselect" class="position-relative">
            <div
              class="form-select subsystem-select entity-binding-control rounded-small p-2 shadow-sm d-flex align-items-center justify-content-between cursor-pointer user-select-none"
              :class="{ disabled: forms_loading || creating_form_binding }"
              @click="toggle_form_dropdown"
            >
              <span :class="selected_form_count ? 'text-dark' : 'text-muted'">
                {{ selected_form_label }}
              </span>
              <i class="bi bi-chevron-down ms-2 small"></i>
            </div>

            <div
              v-if="form_dropdown_open"
              class="position-absolute start-0 end-0 bg-white border rounded-small mt-1 overflow-auto entity-template-dropdown"
            >
              <div v-if="forms_loading" class="px-3 py-2 text-muted small">Loading document forms...</div>
              <div v-else-if="!bindable_forms.length" class="px-3 py-2 text-muted small">No document forms available</div>
              <label
                v-else
                v-for="form in bindable_forms"
                :key="form.id"
                class="d-flex align-items-start gap-2 px-3 py-2 mb-0 text-xs cursor-pointer"
                :class="selected_form_ids.includes(String(form.id)) ? 'bg-light' : ''"
              >
                <input
                  type="checkbox"
                  :value="String(form.id)"
                  v-model="selected_form_ids"
                  class="form-check-input mt-1 flex-shrink-0"
                />
                <span class="d-flex flex-column">
                  <span>{{ form.form_name }}</span>
                  <span class="text-muted small">
                    {{ form.form_type_name || 'Document Form' }}
                  </span>
                </span>
              </label>
            </div>
          </div>
        </div>

        <div class="col-12 col-lg-3">
          <button
            type="button"
            class="btn entity-create-binding-btn rounded-small shadow-sm px-3 py-2 fw-semibold border-0 text-white w-100"
            :disabled="creating_form_binding || !selected_form_count"
            @click="add_document_form_bindings"
          >
            <i class="bi bi-link-45deg me-2"></i>
            {{ creating_form_binding ? 'Binding...' : 'Bind Form' }}
          </button>
        </div>
      </div>
    </div>

    <div class="d-flex align-items-center justify-content-between gap-2 mb-2">
      <div class="fw-bold small">Current Document Form Bindings</div>
      <div class="entity-panel-caption small fw-semibold">{{ bound_form_count }} configured</div>
    </div>

    <div class="d-flex flex-column gap-2">
      <div
        v-for="binding in document_form_bindings"
        :key="binding.id"
        class="entity-binding-list-card bg-white d-flex align-items-start justify-content-between gap-3 rounded-4 p-3 shadow-sm"
      >
        <div class="d-flex align-items-start gap-3 flex-grow-1">
          <div class="entity-summary-icon rounded-3 d-inline-flex align-items-center justify-content-center flex-shrink-0">
            <i class="bi bi-file-earmark-text-fill"></i>
          </div>
          <div class="flex-grow-1">
            <div class="d-flex flex-wrap align-items-center gap-2 mb-1">
              <div class="fw-bold small">{{ binding.form_name }}</div>
              <span class="fw-bold small text-uppercase entity-mini-label">Priority {{ binding.priority || 0 }}</span>
            </div>
            <div class="small text-secondary">
              {{ describe_binding(binding) }}
            </div>
            <div class="small text-secondary mt-1">
              {{ binding.form_type_name || 'Document Form' }}
            </div>
          </div>
        </div>

        <button
          type="button"
          class="btn btn-link btn-sm p-0 text-danger text-decoration-none small fw-semibold"
          :disabled="removing_binding_id === binding.id"
          @click="remove_document_form_binding(binding)"
        >
          <i class="bi bi-trash3 me-1"></i>
          Remove
        </button>
      </div>

      <div v-if="!document_form_bindings.length" class="entity-activity-card rounded-4 p-3 small text-secondary bg-white">
        No document form bindings yet. Create a document form, then bind it here.
      </div>
    </div>
  </div>
</div>

<div class="col-12 col-xl-4">
  <div class="entity-overview-profile-card rounded-4 border shadow p-4 h-100 overflow-hidden">
    <h2 class="entity-overview-title fw-bold mb-3">Documents Context</h2>
    <div class="small mb-4">
      <div class="entity-panel-caption fw-semibold">Viewing document forms for</div>
      <div class="fw-bold">{{ entityTypeName || 'Entity' }}</div>
    </div>

    <div class="d-flex flex-column gap-3 my-4">
      <div class="d-flex align-items-center justify-content-between gap-2">
        <span class="small text-secondary">Available Forms</span>
        <span class="small fw-bold">{{ bindable_form_count }}</span>
      </div>
      <div class="d-flex align-items-center justify-content-between gap-2">
        <span class="small text-secondary">Bound Forms</span>
        <span class="small fw-bold">{{ bound_form_count }}</span>
      </div>
    </div>

    <button
      type="button"
      class="btn entity-overview-profile-btn rounded-small w-100 text-s fw-semibold"
      @click="go_to_entity_overview"
    >
      <i class="bi bi-eye me-2"></i>
      View Entity Overview
    </button>
  </div>
</div>
